// ============================================================
// ANSICHTSSACHE-AGENT – Textfeld
// ============================================================

import { forwardRef } from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { FieldLabel } from "./field-label";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  /** Die leise Zeile darunter — wie beim Eingabefeld nebenan. */
  hint?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const textareaId = id || props.name;

    return (
      <div className="space-y-1.5">
        {label && <FieldLabel htmlFor={textareaId}>{label}</FieldLabel>}
        <textarea
          id={textareaId}
          className={cn(
            "bg-bg-primary text-body-sm text-text-primary flex min-h-[80px] w-full rounded-lg border px-3 py-2",
            "placeholder:text-text-tertiary",
            // Nur `box-shadow` und `border-color` bewegen sich —
            // `transition-all` liess auch die Breite mitfahren, wenn
            // ein Feld in einem Raster neu umbrach.
            "duration-fast ease-standard transition-[box-shadow,border-color]",
            "focus:border-accent/40 focus:ring-2 focus:outline-none",
            "hover:border-border-strong",
            "disabled:cursor-not-allowed disabled:opacity-40",
            "resize-y",
            error ? "border-danger focus:ring-danger/30" : "border-border focus:ring-accent/30",
            className,
          )}
          ref={ref}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? `${textareaId}-error` : hint ? `${textareaId}-hint` : undefined}
          {...props}
        />
        {error && (
          <p
            id={`${textareaId}-error`}
            className="text-caption text-danger flex items-start gap-1.5"
            role="alert"
          >
            <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${textareaId}-hint`} className="text-caption text-text-tertiary">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
